import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Organization, OrganizationPermissions, ProjectPermissions } from '@/features/organizations/api/organization.schemas';
import type { Project } from '@/features/projects/api/projects.schemas';
import type { DateRange } from '@/lib/schemas/dateRange.schemas';

/**
 * Settings store state
 */
interface SettingsState {
  // UI Settings
  theme: 'light' | 'dark';
  language: string;
  notifications: boolean;
  
  // Context Selection
  selectedOrganization: Organization | null;
  selectedProject: Project | null;
  selectedDateRange: DateRange | null;
  
  // Available Options
  organizations: Organization[];
  projects: Project[];
  
  // Permissions
  organizationPermissions: OrganizationPermissions | null;
  projectPermissions: ProjectPermissions | null;
  
  // Actions
  setTheme: (theme: 'light' | 'dark') => void;
  setLanguage: (language: string) => void;
  toggleNotifications: () => void;
  
  // Context Actions
  setSelectedOrganization: (organization: Organization | null) => void;
  setSelectedProject: (project: Project | null) => void;
  setSelectedDateRange: (dateRange: DateRange | null) => void;
  
  // Data Actions
  setOrganizations: (organizations: Organization[]) => void;
  setProjects: (projects: Project[]) => void;
  
  // Permission Actions
  setOrganizationPermissions: (permissions: OrganizationPermissions | null) => void;
  setProjectPermissions: (permissions: ProjectPermissions | null) => void;
  
  // Clear Actions
  clearOrganizationContext: () => void;
  clearProjectContext: () => void;
  clearAllContext: () => void;
}

/**
 * Zustand store for application settings
 * Persisted to localStorage
 */
export const useSettingsStore = create<SettingsState>()(
  persist(
    (set) => ({
      // UI Settings
      theme: 'light',
      language: 'en',
      notifications: true,
      
      // Context Selection
      selectedOrganization: null,
      selectedProject: null,
      selectedDateRange: null,
      
      // Available Options
      organizations: [],
      projects: [],
      
      // Permissions
      organizationPermissions: null,
      projectPermissions: null,
      
      // UI Actions
      setTheme: (theme) => set({ theme }),
      setLanguage: (language) => set({ language }),
      toggleNotifications: () =>
        set((state) => ({ notifications: !state.notifications })),
      
      // Context Actions
      setSelectedOrganization: (organization) => {
        console.log('SettingsStore: Setting selected organization', { 
          organizationId: organization?.id, 
          organizationName: organization?.name 
        });
        set({ 
          selectedOrganization: organization,
          // Clear project context when organization changes
          selectedProject: null,
          projects: [],
          projectPermissions: null,
        });
      },
      
      setSelectedProject: (project) => {
        console.log('SettingsStore: Setting selected project', { 
          projectId: project?.id, 
          projectName: project?.name 
        });
        set({ selectedProject: project });
      },
      
      setSelectedDateRange: (dateRange) => {
        console.log('SettingsStore: Setting selected date range', { 
          from: dateRange?.from, 
          to: dateRange?.to 
        });
        set({ selectedDateRange: dateRange });
      },
      
      // Data Actions
      setOrganizations: (organizations) => {
        console.log('SettingsStore: Setting organizations', { count: organizations.length });
        set({ organizations });
      },
      
      setProjects: (projects) => {
        console.log('SettingsStore: Setting projects', { count: projects.length });
        set({ projects });
      },
      
      // Permission Actions
      setOrganizationPermissions: (permissions) => {
        console.log('SettingsStore: Setting organization permissions', { permissions });
        set({ organizationPermissions: permissions });
      },
      
      setProjectPermissions: (permissions) => {
        console.log('SettingsStore: Setting project permissions', { permissions });
        set({ projectPermissions: permissions });
      },
      
      // Clear Actions
      clearOrganizationContext: () => {
        console.log('SettingsStore: Clearing organization context');
        set({
          selectedOrganization: null,
          selectedProject: null,
          organizations: [],
          projects: [],
          organizationPermissions: null,
          projectPermissions: null,
        });
      },
      
      clearProjectContext: () => {
        console.log('SettingsStore: Clearing project context');
        set({
          selectedProject: null,
          projects: [],
          projectPermissions: null,
        });
      },
      
      clearAllContext: () => {
        console.log('SettingsStore: Clearing all context');
        set({
          selectedOrganization: null,
          selectedProject: null,
          selectedDateRange: null,
          organizations: [],
          projects: [],
          organizationPermissions: null,
          projectPermissions: null,
        });
      },
    }),
    {
      name: 'settings-storage',
      version: 1, // Add version for future migrations
      partialize: (state) => ({
        theme: state.theme,
        language: state.language,
        notifications: state.notifications,
        selectedOrganization: state.selectedOrganization,
        selectedProject: state.selectedProject,
        selectedDateRange: state.selectedDateRange,
      }),
      // Add error handling for storage operations
      storage: {
        getItem: (name) => {
          try {
            const item = localStorage.getItem(name);
            return item ? JSON.parse(item) : null;
          } catch (error) {
            console.error('SettingsStore: Error reading from localStorage:', error);
            // Clear corrupted data
            localStorage.removeItem(name);
            return null;
          }
        },
        setItem: (name, value) => {
          try {
            localStorage.setItem(name, JSON.stringify(value));
          } catch (error) {
            console.error('SettingsStore: Error writing to localStorage:', error);
          }
        },
        removeItem: (name) => {
          try {
            localStorage.removeItem(name);
          } catch (error) {
            console.error('SettingsStore: Error removing from localStorage:', error);
          }
        },
      },
      onRehydrateStorage: () => (state) => {
        console.log('SettingsStore: Rehydrated state:', {
          hasOrganization: !!state?.selectedOrganization,
          hasProject: !!state?.selectedProject,
          hasDateRange: !!state?.selectedDateRange,
        });

        // Convert date strings back to Date objects after rehydration with error handling
        if (state?.selectedDateRange) {
          try {
            const range = state.selectedDateRange;
            if (typeof range.from === 'string') {
              const fromDate = new Date(range.from);
              if (isNaN(fromDate.getTime())) {
                console.warn('SettingsStore: Invalid from date, clearing date range');
                state.selectedDateRange = null;
                return;
              }
              range.from = fromDate;
            }
            if (typeof range.to === 'string') {
              const toDate = new Date(range.to);
              if (isNaN(toDate.getTime())) {
                console.warn('SettingsStore: Invalid to date, clearing date range');
                state.selectedDateRange = null;
                return;
              }
              range.to = toDate;
            }
          } catch (error) {
            console.error('SettingsStore: Error rehydrating date range, clearing:', error);
            state.selectedDateRange = null;
          }
        }
      },
    }
  )
);
